import { useMemo, useState } from 'react';
import { Search, X } from 'lucide-react';
import AnchoredPortal from './AnchoredPortal';
import { LEVELS, levelColor, ghostLevelStyle } from './constants';
import { foldText } from '../../utils/fold';
import useAllWorkspaces from './useAllWorkspaces';
import { setUserWorkspaceLevel, removeUserWorkspaceLevel } from '../../api/access';
import useToastStore from '../../store/toastStore';

// Popover "Áreas de <pessoa>" (arquitetura-v2.md §6.5): busca + lista de
// TODAS as áreas da org, cada uma com checkbox + seletor de nível.
// - sem linha (nem herdada, nem explícita): checkbox desmarcado, sem seletor.
// - herdada do grupo (source 'role'): seletor "fantasma"; mudar o nível ou
//   desmarcar cria uma exceção (negação explícita, já que não existe uma
//   linha individual de herança para apagar).
// - explícita (source 'member'): seletor sólido + botão "voltar ao padrão"
//   (DELETE); desmarcar tem o mesmo efeito.
export default function WorkspacesPopover({ anchorRef, user, workspaces, defaultLevel, canEdit, onClose, onChanged }) {
  const { workspaces: allWorkspaces, loading } = useAllWorkspaces();
  const [search, setSearch] = useState('');
  const [pending, setPending] = useState(() => new Set());
  const [errors, setErrors] = useState({});
  const showToast = useToastStore((s) => s.show);

  const byId = useMemo(() => new Map((workspaces || []).map((w) => [w.id, w])), [workspaces]);

  const filtered = useMemo(() => {
    const q = foldText(search.trim());
    const list = q ? allWorkspaces.filter((w) => foldText(w.name).includes(q)) : allWorkspaces;
    return [...list].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [allWorkspaces, search]);

  const setBusy = (id, v) => setPending((prev) => {
    const next = new Set(prev);
    if (v) next.add(id); else next.delete(id);
    return next;
  });
  const setErr = (id, msg) => setErrors((prev) => ({ ...prev, [id]: msg }));

  const runChange = async (ws, action) => {
    setErr(ws.id, null);
    setBusy(ws.id, true);
    try {
      await action();
      await onChanged?.();
    } catch (err) {
      setErr(ws.id, err.message || 'Falha ao salvar');
    } finally {
      setBusy(ws.id, false);
    }
  };

  const handleLevelChange = (ws, level) =>
    runChange(ws, () => setUserWorkspaceLevel(user.id, ws.id, level));

  const handleClear = (ws) =>
    runChange(ws, async () => {
      await removeUserWorkspaceLevel(user.id, ws.id);
      showToast({ message: `Removido. ${user.name} continua com o acesso que o grupo conceder a ${ws.name}.` });
    });

  const handleToggle = (ws, entry) => {
    if (!entry) return handleLevelChange(ws, defaultLevel || 'editor');
    if (entry.source === 'member') return handleClear(ws);
    return runChange(ws, async () => {
      await setUserWorkspaceLevel(user.id, ws.id, 'deny');
      showToast({ message: `${ws.name} marcada como "Sem acesso" para ${user.name} (exceção ao grupo).` });
    });
  };

  return (
    <AnchoredPortal anchorRef={anchorRef} onClose={onClose} width={380} maxHeight="60vh">
      <div className="px-3 py-2.5 border-b border-border/50 shrink-0">
        <div className="text-xs font-semibold text-text-primary mb-2">Áreas de {user.name}</div>
        <div className="relative">
          <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-text-secondary" />
          <input
            autoFocus
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar área…"
            className="w-full bg-main border border-border rounded pl-7 pr-2 py-1.5 text-xs text-text-primary outline-none focus:border-accent/50"
          />
        </div>
      </div>
      <div className="overflow-y-auto flex-1 py-1">
        {loading && <div className="px-3 py-4 text-xs text-text-secondary">Carregando…</div>}
        {!loading && filtered.length === 0 && (
          <div className="px-3 py-4 text-xs text-text-secondary">Nenhuma área encontrada.</div>
        )}
        {filtered.map((ws) => {
          const entry = byId.get(ws.id);
          const checked = !!entry;
          const busy = pending.has(ws.id);
          const err = errors[ws.id];
          const ghost = entry?.source === 'role';
          return (
            <div
              key={ws.id}
              className={`flex items-center gap-2 px-3 py-1.5 hover:bg-white/[0.03] ${busy ? 'opacity-60' : ''} ${err ? 'ring-1 ring-danger ring-inset' : ''}`}
            >
              <input
                type="checkbox"
                checked={checked}
                disabled={busy || !canEdit}
                onChange={() => handleToggle(ws, entry)}
                className="w-3.5 h-3.5 accent-accent shrink-0"
              />
              <span className="flex-1 text-xs text-text-primary truncate" title={ws.name}>{ws.name}</span>
              {entry && (
                <>
                  <select
                    value={entry.level}
                    disabled={busy || !canEdit}
                    onChange={(e) => handleLevelChange(ws, e.target.value)}
                    className="rounded px-1.5 py-1 text-[11px] font-semibold outline-none border shrink-0"
                    style={
                      ghost
                        ? ghostLevelStyle(entry.level)
                        : { backgroundColor: levelColor(entry.level), color: '#fff', borderColor: 'transparent' }
                    }
                  >
                    {LEVELS.map((l) => (
                      <option key={l.value} value={l.value} style={{ color: '#111' }}>{l.label}</option>
                    ))}
                  </select>
                  {ghost ? (
                    <span title="Herdado do grupo" className="text-white/40 text-[11px] w-4 text-center shrink-0">⌐</span>
                  ) : (
                    <button
                      type="button"
                      title="Voltar ao padrão"
                      disabled={busy || !canEdit}
                      onClick={() => handleClear(ws)}
                      className="text-text-secondary hover:text-danger shrink-0"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  )}
                </>
              )}
              {err && (
                <button
                  type="button"
                  title={err}
                  onClick={() => setErr(ws.id, null)}
                  className="text-[10px] text-danger shrink-0 hover:underline"
                >
                  erro
                </button>
              )}
            </div>
          );
        })}
      </div>
    </AnchoredPortal>
  );
}
